/**
 * RecordsTab: container for the four record sub-tabs.
 *
 * Holds everything that's a dated record on this property:
 *   1. Invoices             — invoice ↔ property ↔ room linkage
 *   2. Service Certificates — clinical certificates issued at service
 *   3. Proposals            — commercial offers (ported from Documents)
 *   4. Agreements           — signed legal contracts (ported from Documents)
 *
 * Renamed from ServicesTab on 2026-05-29 because the contents now span
 * commercial + clinical + legal records. The previous Documents tab is
 * dissolved entirely — Proposals + Agreements moved here, Service
 * Certificates already live here, and the POS Certificate + Charter
 * sections it carried were duplicates of the Certification tab.
 *
 * Sub-tab visual pattern mirrors the canonical pos-amb-tab style used
 * across POSAmbassadors, the Charters page, and the previous Services
 * tab. Same shape, same gold underline.
 */

import { useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import InvoicesSubTab from './services/InvoicesSubTab';
import ServiceCertificatesSubTab from './services/ServiceCertificatesSubTab';
import ProposalsSubTab from './services/ProposalsSubTab';
import AgreementsSubTab from './services/AgreementsSubTab';
import type { EnrichedProperty, PropertyMode } from './types';

type SubTabKey = 'invoices' | 'certificates' | 'proposals' | 'agreements';

interface RecordsTabProps {
  supabase: SupabaseClient;
  property: EnrichedProperty;
  mode: PropertyMode;
  onLinked?: () => void;
}

const SUB_TABS: { key: SubTabKey; label: string }[] = [
  { key: 'invoices', label: 'Invoices' },
  { key: 'certificates', label: 'Service Certificates' },
  { key: 'proposals', label: 'Proposals' },
  { key: 'agreements', label: 'Agreements' },
];

export default function RecordsTab({ supabase, property, mode, onLinked }: RecordsTabProps) {
  const [sub, setSub] = useState<SubTabKey>('invoices');

  return (
    <div>
      <div className="pos-amb-tabs pos-amb-tabs--compact" role="tablist" aria-label="Records sub-tabs">
        {SUB_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={sub === t.key}
            className={`pos-amb-tab ${sub === t.key ? 'active' : ''}`}
            onClick={() => setSub(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {sub === 'invoices' && (
        <InvoicesSubTab supabase={supabase} property={property} mode={mode} onLinked={onLinked} />
      )}
      {sub === 'certificates' && (
        <ServiceCertificatesSubTab supabase={supabase} property={property} />
      )}
      {sub === 'proposals' && (
        <ProposalsSubTab supabase={supabase} property={property} />
      )}
      {sub === 'agreements' && (
        <AgreementsSubTab supabase={supabase} property={property} />
      )}
    </div>
  );
}
